import { createContext, useContext, useId, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

export type TabsVariant = 'underline' | 'pill';

interface TabsContextValue {
  value: string;
  setValue: (value: string) => void;
  baseId: string;
  variant: TabsVariant;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabs(component: string): TabsContextValue {
  const context = useContext(TabsContext);
  if (!context) throw new Error(`<${component}> must be used inside <Tabs>`);
  return context;
}

export interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  className?: string;
  variant?: TabsVariant;
}

/** Controlled tabs — the composer keys them by platform id. */
export function Tabs({ value, onValueChange, children, className, variant = 'underline' }: TabsProps) {
  const baseId = useId();
  return (
    <TabsContext.Provider value={{ value, setValue: onValueChange, baseId, variant }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ children, className }: { children: ReactNode; className?: string }) {
  const tabs = useTabs('TabsList');
  const listRef = useRef<HTMLDivElement>(null);

  // Left/Right move between tabs, as the WAI-ARIA tabs pattern expects.
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const triggers = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]:not([disabled])') ?? [],
    );
    if (!triggers.length) return;
    event.preventDefault();
    const index = triggers.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? triggers.length - 1
          : event.key === 'ArrowRight'
            ? (index + 1 + triggers.length) % triggers.length
            : (index - 1 + triggers.length) % triggers.length;
    triggers[next]?.focus();
    triggers[next]?.click();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      onKeyDown={onKeyDown}
      className={cn(
        'flex items-center overflow-x-auto',
        tabs.variant === 'underline'
          ? 'gap-1 border-b border-border'
          : 'gap-1 rounded-lg border border-border bg-elevated p-1',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  /** Small count or status dot rendered after the label. */
  badge?: ReactNode;
}

export function TabsTrigger({ value, children, className, disabled, badge }: TabsTriggerProps) {
  const tabs = useTabs('TabsTrigger');
  const selected = tabs.value === value;
  const underline = tabs.variant === 'underline';

  return (
    <button
      type="button"
      role="tab"
      id={`${tabs.baseId}-tab-${value}`}
      aria-controls={`${tabs.baseId}-panel-${value}`}
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      onClick={() => tabs.setValue(value)}
      className={cn(
        'relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-medium transition-colors',
        underline
          ? cn('-mb-px px-3 py-2.5', selected ? 'text-fg' : 'text-muted hover:text-fg')
          : cn('rounded-md px-3 py-1.5', selected ? 'text-fg' : 'text-muted hover:text-fg'),
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      {/* The indicator is one element that slides between triggers. */}
      {selected && (
        <motion.span
          layoutId={`${tabs.baseId}-indicator`}
          transition={{ type: 'spring', stiffness: 520, damping: 42, mass: 0.7 }}
          className={cn(
            'absolute',
            underline
              ? 'inset-x-0 bottom-0 h-0.5 rounded-full bg-accent'
              : 'inset-0 rounded-md border border-border bg-surface shadow-hair',
          )}
          aria-hidden
        />
      )}
      <span className="relative z-10 inline-flex items-center gap-2">
        {children}
        {badge}
      </span>
    </button>
  );
}

export function TabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  const tabs = useTabs('TabsContent');
  if (tabs.value !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${tabs.baseId}-panel-${value}`}
      aria-labelledby={`${tabs.baseId}-tab-${value}`}
      tabIndex={0}
      className={cn('animate-[var(--animate-fade-in)] pt-4 outline-none', className)}
    >
      {children}
    </div>
  );
}
